import AppObserver from "@app/middlewares/appObserver"
import { store } from "@app/store"
import { selectAccessToken } from "../login/loginSelectors"
import { createAppAsyncThunk } from "@app/createAppAsyncThunk"
import { LoginClient } from "@clients/loginClient"

export const accessTokenObserver: AppObserver = {
  didUpdate: (previousState, currentState, action) => {
    const prevAccessToken = selectAccessToken(previousState)
    const newAccessToken = selectAccessToken(currentState)
    const isInitialLoading = currentState.login.user === "not-loaded"

    if (prevAccessToken === newAccessToken && !isInitialLoading) {
      return
    }
    setAccessTokenInLocalStorage(newAccessToken)
    if (newAccessToken) {
      store.dispatch(fetchUserForAccessToken(newAccessToken))
    }
  },
}

const ACCESS_TOKEN_KEY = "accessToken"
const IMPERSONATION_FLAG = "ms-impersonation"

// A tab opened via one-click "view as" keeps its token in sessionStorage
// (flag set by LoginProxyToken before the exchange), so the admin's own
// localStorage session in every other tab stays untouched.
export function isImpersonatedSession(): boolean {
  try {
    return sessionStorage.getItem(IMPERSONATION_FLAG) === "1"
  } catch {
    return false
  }
}

export function markSessionAsImpersonated() {
  try {
    sessionStorage.setItem(IMPERSONATION_FLAG, "1")
  } catch {
    // storage unavailable: degrade to a normal (localStorage) login
  }
}

export function getAccessTokenFromLocalStorage() {
  if (isImpersonatedSession()) {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY)
  }
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function setAccessTokenInLocalStorage(accessToken: string | null) {
  if (isImpersonatedSession()) {
    if (accessToken) {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    } else {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY)
      sessionStorage.removeItem(IMPERSONATION_FLAG)
    }
    return
  }
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
  }
}

export const fetchUserForAccessToken = createAppAsyncThunk(
  "login/fetchUserForAccessToken",
  async (accessToken: string) => {
    return LoginClient.getUser(accessToken).then((user) => {
      return { accessToken, ...user }
    })
  },
)
